// Count Together's stage break, as a list: the count and this llama's own
// answer on top, then a row per upgrade card with how many of the phones
// here picked it. The row this llama picked wears its colour. A press picks,
// a press on the picked row takes the pick back, and the ask row counts the
// vote down on the room's clock. When the vote closes the party's pick wears
// the colour and the rows stop taking presses. After the last stage there
// are no cards: the top of the stage's board hangs instead. The game's side
// is `party_upgrade_vote.gd`.

import type { Activity } from '../../protocol';
import { buzz, el } from '../dom';
import { type ListRow, list } from '../list';
import type { Page } from '../page';
import { say } from '../say';
import { play } from '../sfx';
import { roomTime } from '../state';
import { STRINGS } from '../strings';

interface StageCard {
  name: string;
  description: string;
}

// [seat, guess, error, left, name]: the name only on the top rows.
type StageRow = [number, number, number, number, string?];

// The data as the game sends it (`party_upgrade_vote.gd`, `data`).
interface StageData {
  count: number;
  rows: StageRow[];
  cards: StageCard[];
  picks: Record<string, number>;
  need: number;
  ends_at: number;
  chosen: number;
}

const readStage = (activity: Activity): StageData => {
  const data = (activity.data ?? {}) as Partial<StageData>;
  return {
    count: data.count ?? 0,
    rows: Array.isArray(data.rows) ? data.rows : [],
    cards: Array.isArray(data.cards) ? data.cards : [],
    picks: data.picks ?? {},
    need: data.need ?? 0,
    ends_at: data.ends_at ?? 0,
    chosen: data.chosen ?? -1,
  };
};

function mine(stage: StageData, seat: number | null): HTMLElement {
  const row = stage.rows.find((r) => r[0] === seat);
  if (!row) return say(STRINGS.stage.none, 'note');
  const [, guess, error, left] = row;
  return say(STRINGS.stage.mine(guess, error, left), error === 0 ? 'good' : 'line');
}

function board(stage: StageData, seat: number | null): HTMLElement {
  const rows: ListRow[] = stage.rows
    .filter((row) => row[4] !== undefined)
    .map((row, at) => ({
      lead: String(at + 1),
      name: row[4] ?? '',
      value: String(row[3]),
      mine: row[0] === seat,
    }));
  return list(rows);
}

function cards(page: Page, activity: Activity, stage: StageData, picked: number): HTMLElement {
  const counts = stage.cards.map(() => 0);
  for (const index of Object.values(stage.picks)) {
    if (index < counts.length) counts[index] = (counts[index] ?? 0) + 1;
  }
  const closed = activity.phase === 'closed';
  const rows: ListRow[] = stage.cards.map((card, index) => ({
    name: card.name,
    value: stage.need > 0 ? STRINGS.stage.picked(counts[index] ?? 0, stage.need) : '',
    picked: closed ? index === stage.chosen : index === picked,
    onPress: closed
      ? undefined
      : () => {
          buzz();
          play('click');
          page.send({
            t: 'play',
            id: activity.id,
            data: { pick: index === picked ? null : index },
          });
        },
  }));
  return list(rows, 'vote-list');
}

export function buildStage(page: Page, activity: Activity): HTMLElement {
  const state = page.state();
  const stage = readStage(activity);
  const body = el('div', 'body body-vote');
  body.append(say(STRINGS.stage.count(stage.count), 'label'), mine(stage, state.seat));
  if (activity.phase === 'board') {
    body.append(board(stage, state.seat));
    return body;
  }
  const ask = el('div', 'ask');
  if (activity.phase === 'closed') {
    ask.append(say(STRINGS.stage.chosen(stage.cards[stage.chosen]?.name ?? ''), 'label'));
  } else {
    ask.append(say(STRINGS.stage.ask, 'label'), el('p', 'vote-countdown'));
  }
  const picked = state.seat === null ? -1 : (stage.picks[String(state.seat)] ?? -1);
  body.append(ask, cards(page, activity, stage, picked));
  const card = stage.cards[activity.phase === 'closed' ? stage.chosen : picked];
  if (card) body.append(say(card.description, 'note'));
  return body;
}

// The vote's countdown, in whole seconds on the room's clock, redrawn in place.
export function liveStage(page: Page, root: HTMLElement, activity: Activity): void {
  const holder = root.querySelector<HTMLElement>('.vote-countdown');
  if (!holder) return;
  const stage = readStage(activity);
  const left = Math.max(1, Math.ceil((stage.ends_at - roomTime(page.state(), Date.now())) / 1000));
  const text = String(left);
  if (holder.textContent !== text) holder.textContent = text;
}
